import { ConnectComponentsProvider } from "@stripe/react-connect-js";
import { t } from "@travily/i18n";
import { sectionTab, sectionTabList } from "@ui/components/SectionTabs";
import { tv } from "@ui/lib/tv";
import { Link } from "@ui/primitives/Link";
import type { ReactNode } from "react";
import { type UseStripeConnectInstanceOptions, useStripeConnectInstance } from "./use-stripe-connect-instance";

const payoutsFrameStyles = tv({
  slots: {
    root: "flex flex-1 flex-col gap-6",
    contentPadding: "flex flex-1 flex-col p-5 md:p-8",
    contentWrapper: "mx-auto flex w-full max-w-3xl flex-1 flex-col gap-6",
  },
});

export type PayoutsTabKey = "overview" | "sales" | "manage" | "documents";

export interface PayoutsTab {
  key: PayoutsTabKey;
  label: string;
  href: string;
}

export interface PayoutsFrameProps extends UseStripeConnectInstanceOptions {
  /** Currently-active tab. */
  currentTab: PayoutsTabKey;
  /** Tab definitions; allows the consumer to provide localized labels and hrefs. */
  tabs: PayoutsTab[];
  /** Embedded Stripe Connect components for the active tab. */
  children: ReactNode;
}

/**
 * Sub-app frame for every Payouts tab. Renders inside a host `PageFrame` so
 * the top bar and creator-mode chrome flow through from the rest of the app.
 *
 * Owns the four-tab sub-nav and wraps Stripe's embedded components in a
 * `ConnectComponentsProvider`. Tabs navigate as full Inertia page reloads,
 * so each tab gets a fresh runtime keyed off the freshly-minted server-side
 * client_secret.
 */
export function PayoutsFrame({
  children,
  currentTab,
  fetchRefreshedClientSecret,
  initialClientSecret,
  publishableKey,
  tabs,
}: PayoutsFrameProps) {
  const styles = payoutsFrameStyles();
  const stripeConnectInstance = useStripeConnectInstance({
    fetchRefreshedClientSecret,
    initialClientSecret,
    publishableKey,
  });

  return (
    <div className={styles.root()}>
      <nav
        aria-label={t("components.frames.payouts.nav_label")}
        className={sectionTabList({ size: "sm", className: "pt-4 md:px-8" })}
      >
        {tabs.map((tab) => {
          const active = tab.key === currentTab;
          return (
            <Link
              aria-current={active ? "page" : undefined}
              className={sectionTab({ size: "sm" })}
              data-selected={active || undefined}
              href={tab.href}
              key={tab.key}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>

      <div className={styles.contentPadding()}>
        <div className={styles.contentWrapper()}>
          {stripeConnectInstance ? (
            <ConnectComponentsProvider connectInstance={stripeConnectInstance}>{children}</ConnectComponentsProvider>
          ) : null}
        </div>
      </div>
    </div>
  );
}
